import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputBase from '@mui/material/InputBase';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { styled, SxProps } from '@mui/material/styles';

// Styled input component with custom styling
const BootstrapInput = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: (theme.vars ?? theme).palette.background.paper,
    border: '1px solid #ced4da',
    fontSize: 16,
    padding: '10px 26px 10px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    // Use the system font instead of the default Roboto font.
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    '&:focus': {
      borderRadius: 4,
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
}));

export interface SelectOption {
  value: string | number;
  label: string;
}

export interface CustomSelectProps {
  label?: string;
  value: string | number;
  onChange: (value: string | number) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  sx?: SxProps;
  // Label positioning and styling
  labelPosition?: 'left' | 'top';
  labelColor?: string;
  labelSize?: 'small' | 'medium' | 'large';
  // Allow custom label positioning
  showLabel?: boolean;
  labelId?: string;
  id?: string;
}

/**
 * CustomSelect - A flexible select component with built-in label positioning
 * Label can be positioned to the left (vertically stacked) or top
 * Uses KeyboardArrowDown icon instead of default Material-UI arrow
 */
export const CustomSelect = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  size = 'medium',
  fullWidth = false,
  variant = 'standard',
  sx,
  labelPosition = 'top',
  labelColor = '#000000',
  labelSize = 'medium',
  showLabel = true,
  labelId,
  id,
}: CustomSelectProps) => {
  const handleChange = (event: { target: { value: string | number } }) => {
    onChange(event.target.value);
  };

  const renderSelectedValue = (selected: unknown) => {
    if (
      placeholder &&
      (selected === '' || selected === null || selected === undefined)
    ) {
      return (
        <Typography
          sx={{
            color: (theme) => theme.palette.text.disabled,
          }}
        >
          {placeholder}
        </Typography>
      );
    }

    const matchedOption = options.find(
      (option) => String(option.value) === String(selected),
    );

    return matchedOption ? matchedOption.label : (selected as string | number);
  };

  // If no label should be shown, return just the Select
  if (!showLabel || !label) {
    return (
      <Select
        id={id}
        value={value}
        onChange={handleChange}
        input={<BootstrapInput />}
        size={size}
        disabled={disabled}
        sx={sx}
        IconComponent={KeyboardArrowDown}
        displayEmpty
        renderValue={renderSelectedValue}
      >
        {placeholder && (
          <MenuItem value="" disabled>
            <em>{placeholder}</em>
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    );
  }

  // With label - can be positioned left (vertically stacked) or top
  if (labelPosition === 'left') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ...sx }}>
        <Typography
          variant={
            labelSize === 'small'
              ? 'body2'
              : labelSize === 'large'
                ? 'h6'
                : 'body1'
          }
          sx={{
            color: labelColor,
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
        <Select
          id={id}
          value={value}
          onChange={handleChange}
          input={<BootstrapInput />}
          size={size}
          disabled={disabled}
          fullWidth={fullWidth}
          IconComponent={KeyboardArrowDown}
          displayEmpty
          renderValue={renderSelectedValue}
        >
          {placeholder && (
            <MenuItem value="" disabled>
              <em>{placeholder}</em>
            </MenuItem>
          )}
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  }

  // Default: Label on top (like your original)
  return (
    <FormControl
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      sx={sx}
    >
      <InputLabel id={labelId} htmlFor={id} sx={{ color: labelColor }}>
        {label}
      </InputLabel>
      <Select
        id={id}
        labelId={labelId}
        value={value}
        onChange={handleChange}
        input={<BootstrapInput />}
        IconComponent={KeyboardArrowDown}
        displayEmpty
        renderValue={renderSelectedValue}
      >
        {placeholder && (
          <MenuItem value="" disabled>
            <em>{placeholder}</em>
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
